import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { inr } from "@/lib/api";
import { PriceTag } from "@/components/PriceTag";
import { CalendarDays, Briefcase, ChevronDown } from "lucide-react";

const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

const GroupItem = ({ g }) => (
  <div data-testid={`tc-info-group-${g.id}`} className="rounded-lg border border-border/60 bg-card/60 p-3">
    <p className="text-sm text-white font-semibold">{g.name}</p>
    <p className="text-xs text-gold mt-0.5 flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {fmt(g.date)}{g.package ? ` · ${g.package}` : ""}</p>
    <div className="flex items-center justify-between mt-2">
      <span className="font-serif text-lg text-gold" data-testid={`tc-info-group-price-${g.id}`}>{g.price ? <PriceTag price={g.price} mrp={g.mrp} until={g.offer_until} /> : "Price on request"}</span>
      <span className={`text-[11px] ${g.seats_left === 0 ? "text-red-300" : "text-emerald-300"}`}>{g.seats_left === null ? "Seats open" : g.seats_left === 0 ? "Full" : `${g.seats_left} seats left`}</span>
    </div>
  </div>
);

const JobItem = ({ j }) => (
  <div data-testid={`tc-info-job-${j.id}`} className="rounded-lg border border-border/60 bg-card/60 p-3">
    <p className="text-sm text-white font-semibold">{j.post}</p>
    <p className="text-xs text-muted-foreground mt-0.5">{j.company} · {j.country}</p>
    <div className="flex items-center justify-between mt-2 text-xs">
      <span className="text-emerald-300">{j.salary ? `Salary: ${j.salary}` : "Salary on request"}</span>
      <span className="text-gold">Charge: <PriceTag price={j.service_charge} mrp={j.service_charge_mrp} /></span>
    </div>
    {j.vacancies ? <p className="text-[11px] text-muted-foreground mt-1">{j.vacancies} vacancies</p> : null}
  </div>
);

const PackageItem = ({ p }) => (
  <div data-testid={`tc-info-package-${p.id}`} className="rounded-lg border border-gold/40 bg-gold/5 p-3">
    <p className="text-sm text-white font-semibold">{p.name}</p>
    <p className="text-[11px] text-muted-foreground mt-0.5">{p.desc}</p>
    <p className="mt-1"><PriceTag price={p.price} mrp={p.mrp} until={p.offer_until} /> <span className="text-[10px] font-sans text-muted-foreground">per person</span></p>
  </div>
);

export const InfoPanel = ({ client, cat }) => {
  const [info, setInfo] = useState({ groups: [], jobs: [], packages: [] });
  const [open, setOpen] = useState(true);
  useEffect(() => { client.get("/telecaller/info").then((r) => setInfo(r.data)).catch(() => {}); }, [client]);
  const isJob = cat === "job";
  const items = isJob ? info.jobs : info.groups;
  return (
    <Card className="bg-card/40 border-gold/20 p-4" data-testid="tc-info-panel">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between" data-testid="tc-info-toggle">
        <span className="eyebrow text-gold flex items-center gap-1.5">
          {isJob ? <Briefcase className="w-3.5 h-3.5" /> : <CalendarDays className="w-3.5 h-3.5" />}
          {isJob ? `All job listings with cost (${items.length})` : `Umrah packages, groups, dates & prices`}
        </span>
        <ChevronDown className={`w-4 h-4 text-gold transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && !isJob && (info.packages || []).length > 0 && (
        <div className="mt-3">
          <p className="eyebrow text-muted-foreground mb-2">Package details & cost</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2.5">{info.packages.map((p) => <PackageItem key={p.id} p={p} />)}</div>
          <p className="eyebrow text-muted-foreground mt-3">Group dates & price</p>
        </div>
      )}
      {open && (
        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-2.5 max-h-72 overflow-y-auto">
          {items.length === 0 && <p className="text-xs text-muted-foreground col-span-full">{isJob ? "No active job listings yet." : "No upcoming groups yet."}</p>}
          {isJob ? items.map((j) => <JobItem key={j.id} j={j} />) : items.map((g) => <GroupItem key={g.id} g={g} />)}
        </div>
      )}
    </Card>
  );
};
